import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import leoWave from "@/assets/leo-mascot-wave.png";
import leoCheer from "@/assets/leo-mascot-cheer.png";
import leoBook from "@/assets/leo-mascot-book.png";
import leoPoint from "@/assets/leo-mascot-point.png";

export type LeoPose = "wave" | "cheer" | "book" | "point";
export type LeoSize = "sm" | "md" | "lg" | "xl";
export type LeoPosition =
  | "inline"
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left";

interface LeoMascotProps {
  pose?: LeoPose;
  size?: LeoSize;
  position?: LeoPosition;
  message?: string;
  flip?: boolean;
  animation?: "float" | "bounce" | "wiggle" | "none";
  className?: string;
}

const poseMap: Record<LeoPose, string> = {
  wave: leoWave,
  cheer: leoCheer,
  book: leoBook,
  point: leoPoint,
};

const sizeMap: Record<LeoSize, string> = {
  sm: "w-24 md:w-32",
  md: "w-36 md:w-48",
  lg: "w-48 md:w-64",
  xl: "w-64 md:w-80",
};

const positionMap: Record<LeoPosition, string> = {
  inline: "",
  "bottom-right":
    "fixed bottom-4 right-4 md:bottom-6 md:right-6 z-30 pointer-events-none",
  "bottom-left":
    "fixed bottom-4 left-4 md:bottom-6 md:left-6 z-30 pointer-events-none",
  "top-right": "absolute top-4 right-4 md:top-6 md:right-6 z-20",
  "top-left": "absolute top-4 left-4 md:top-6 md:left-6 z-20",
};

const animations = {
  float: { y: [0, -12, 0], rotate: [0, 2, -2, 0] },
  bounce: { y: [0, -18, 0], scale: [1, 1.05, 1] },
  wiggle: { rotate: [0, -6, 6, -6, 0] },
  none: {},
};

const LeoMascot: React.FC<LeoMascotProps> = ({
  pose = "wave",
  size = "md",
  position = "inline",
  message,
  flip = false,
  animation = "float",
  className,
}) => {
  const src = poseMap[pose];
  const anim = animations[animation];

  return (
    <div className={cn(positionMap[position], className)}>
      <div className="relative inline-flex flex-col items-center">
        {message && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="relative mb-2 max-w-[220px] rounded-2xl border-2 border-amber-300 bg-white px-4 py-2 shadow-lg"
          >
            <p className="text-center text-sm font-bold text-amber-900 font-fredoka leading-snug">
              {message}
            </p>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 border-b-2 border-r-2 border-amber-300 bg-white" />
          </motion.div>
        )}

        <motion.img
          src={src}
          alt="Leo the Lion - TinyMinds mascot"
          loading="lazy"
          width={1024}
          height={1024}
          className={cn(
            sizeMap[size],
            "h-auto object-contain drop-shadow-2xl",
            flip && "scale-x-[-1]"
          )}
          animate={anim}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          whileHover={{ scale: 1.08, rotate: flip ? -5 : 5 }}
        />
      </div>
    </div>
  );
};

export default React.memo(LeoMascot);
